import { useParams, Navigate, Link } from "react-router-dom";
import { Clock, Target, ListChecks, Check, ArrowLeft } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal, MaskLine } from "@/components/Reveal";
import { SectionLabel, GoldButton } from "@/components/ui-kit";
import { FinalCTA, ScheduleTable } from "@/components/blocks";
import { courseBySlug, images } from "@/data/site";

const catImage = { fitness: images.fitnessClass, olistica: images.holistic, balli: images.fitnessClass };
const catLabel = { fitness: "Corso Fitness", olistica: "Sala Olistica", balli: "Balli & Movimento" };

export default function CorsoDetail() {
  const { slug } = useParams();
  const course = courseBySlug(slug);
  if (!course) return <Navigate to="/corsi-fitness" replace />;

  const isOlistica = course.category === "olistica";

  return (
    <>
      <Seo title={`${course.name} a Lusciano | Body Evolution 2.0`} description={`${course.name} a Body Evolution 2.0 Lusciano: ${course.short} Durata 50 minuti. Prenota una prova gratuita.`} />

      <section className="relative h-[60vh] min-h-[440px] flex items-end grain overflow-hidden" data-testid="course-hero">
        <img src={catImage[course.category]} alt={course.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/30" />
        <div className="relative z-10 max-w-[1400px] mx-auto w-full px-5 lg:px-10 pb-14">
          <Link to={course.category === "olistica" ? "/sala-olistica" : "/corsi-fitness"} className="inline-flex items-center gap-2 text-ivory/60 hover:text-gold text-sm mb-6"><ArrowLeft size={16} /> Torna ai corsi</Link>
          <Reveal><SectionLabel dark>{catLabel[course.category]}</SectionLabel></Reveal>
          <h1 className="mt-4 text-ivory font-display font-medium tracking-tight text-5xl sm:text-6xl lg:text-7xl">
            <MaskLine delay={0.1}>{course.name}</MaskLine>
          </h1>
          <Reveal delay={0.3}><span className="mt-5 inline-flex items-center gap-2 text-gold text-sm font-semibold"><Clock size={16} /> Durata 50 minuti</span></Reveal>
        </div>
      </section>

      <section className="bg-ivory py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7">
            <Reveal><p className="text-ink-800/80 text-xl lg:text-2xl font-display font-medium leading-snug">{course.intro}</p></Reveal>

            <Reveal delay={0.1}>
              <div className="mt-12">
                <h2 className="flex items-center gap-3 font-display text-2xl text-ink-800"><Target size={22} className="text-gold" /> A chi è rivolto</h2>
                <p className="mt-3 text-ink-800/70 leading-relaxed">{course.audience}</p>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-10">
                <h2 className="flex items-center gap-3 font-display text-2xl text-ink-800"><ListChecks size={22} className="text-gold" /> Come si svolge la lezione</h2>
                <p className="mt-3 text-ink-800/70 leading-relaxed">{course.how}</p>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-10">
                <h2 className="font-display text-2xl text-ink-800">Benefici</h2>
                <div className="mt-4 flex flex-wrap gap-2.5">
                  {course.benefits.map((b) => (
                    <span key={b} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sand border border-black/10 text-sm text-ink-800/80"><Check size={15} className="text-gold" /> {b}</span>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="sticky top-28">
                <div className="label text-gold mb-4">Orari</div>
                <ScheduleTable schedule={course.schedule} />
                <div className={`mt-6 rounded-sm p-6 ${isOlistica ? "bg-sand" : "bg-ink text-ivory grain"} relative overflow-hidden`}>
                  <div className="relative z-10">
                    <p className={`text-sm ${isOlistica ? "text-ink-800/70" : "text-ivory/70"}`}>Vuoi provare {course.name}? La prima lezione è gratuita.</p>
                    <GoldButton to="/contatti" className="mt-4 w-full">Prenota una prova gratuita</GoldButton>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
